import React, { useState, useEffect, useRef } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  ModelBase, 
} from "@models";
import { 
  Character, 
  CharacterFeatureSource, 
  Proficiency,
  Tool,
  Weapon,
  // Item,
  CharacterSheetInterface,
  CharacterFeature,
  ARMOR_TYPES,
  WEAPON_KEYWORDS
} from "@dndModels";

// import API from '../../../utilities/system_api';
import CommonUtilities from "@utilities/common_utilities";
import DataUtilities from '@dndUtilities/data_utilities';

import DisplayObjects from "@dndComponents/display/DisplayObjects";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import CharacterFeatureFromSource from "@dndComponents/character/features/CharacterFeatureFromSource";
import FloatMenu from "@components/input/menus/FloatMenu";
import StringBox from "@components/input/strings/StringBox";
import LoadingBox from "@components/input/LoadingBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";

interface Props {
  character: Character;
  char_interface: CharacterSheetInterface;
  disabled: boolean;
}

// const api = API.getInstance();
const common_util = CommonUtilities.getInstance();
const data_util = DataUtilities.getInstance();

export default function CharacterProficiencies({
  character,
  char_interface,
  disabled,
}: Props) {
  const [fetched, setFetched] = useState(false);
  // const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [weapons, setWeapons] = useState<Weapon[]>([]);
  const [tools, setTools] = useState<Tool[]>([]);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("Features");
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const recalculated = () => {
    // console.log("Proficiencies");
    setReloading(true);
  }

  // eslint-disable-next-line
  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      character.subscription_holder.subscribe("Recalculated", mounted, recalculated);
      // character.subscription_holder.subscribe("Skill", mounted, recalculated);
      load();
    }
  });

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const load = () => {
    setTools(data_util.tools);
    setWeapons((data_util.items).filter(o => o instanceof Weapon) as Weapon[]);
  }

  const renderProficiencyFeatures = () => {
    const proficiency_features = character.mind.proficiency_features.filter(cf => 
      cf.floating_feature instanceof Proficiency && 
        common_util.anyOverlaps(cf.floating_feature.proficiency_types, ["Tool","Armor","Weapon","Special"]));
    const wm_features = character.mind.weapon_mastery_features;
    const the_features = [...proficiency_features, ...wm_features];
    const use_features: CharacterFeature[] = [];
    for (const cf of the_features) {
      if (use_features.filter(o => o.cfb_id === cf.cfb_id).length === 0) {
        use_features.push(cf);
      }
    }
    return (
      <>
        <Grid
          className="header centered"
          size={{
            xs: 12
          }}>
          Proficiency Features
        </Grid>
        { use_features.sort((a,b) => { return a.name.localeCompare(b.name) }).map((char_feature, key) => {
          const source = character.char_feature_sources.find(o => 
            o.true_id === char_feature.source_true_id);
          if (source) {
            return (
              <Grid
                key={key}
                size={{
                  xs: 12
                }}>
                <CharacterFeatureFromSource
                  character={character}
                  obj={char_feature}
                  source={source}
                />
              </Grid>
            );
          }
          return null;
        })}
      </>
    );
  }

  const renderProficiencyCustomizations = () => {
    let prof_type = view.substring(10);
    prof_type = prof_type.substring(0, prof_type.length - 1);
    let data_set: (ModelBase | Tool)[] = [];
    if (prof_type === "Armor") {
      data_set = ARMOR_TYPES;
    } else if (prof_type === "Weapon") {
      data_set = [...WEAPON_KEYWORDS,...weapons];
    } else if (prof_type === "Tool") {
      data_set = tools;
    } 
    return (
      <>
        <Grid
          className="header centered"
          size={{
            xs: 12
          }}>
          { view }
        </Grid>
        <Grid
          container
          spacing={0}
          direction="row"
          size={{
            xs: 12
          }}>
          { data_set.map(prof_obj => {
            return (
              <Grid
                key={prof_obj._id}
                container
                spacing={0}
                direction="row"
                size={{
                  xs: 12
                }}>{ renderProficiencyCustomization(prof_type, prof_obj) }</Grid>
            );
          }) }
        </Grid>
      </>
    );
  }
  
  const renderProficiencyCustomization = (prof_type: string, prof_obj: (ModelBase | Tool)) => {
    // const obj_id = prof_obj._id;
    // const parent = char_interface.non_skill_prof_customizations[prof_type];
    // let customization: { id: string, proficiency: boolean | null, notes: string } = { id: obj_id, proficiency: null, notes: "" };
    // if (parent) {
    //   const customization_maybe = parent.find(o => o.id === obj_id);
    //   if (customization_maybe) {
    //     customization = customization_maybe;
    //   }
    // }
    // let prof = false;
    // if (prof_type === "Armor") {
    //   const proficiency = char_interface.armor_proficiencies.find(o => o.proficiency_id === obj_id);
    //   if (proficiency) {
    //     prof = true;
    //   }
    // } else if (prof_type === "Weapon") {
    //   let proficiency = char_interface.weapon_proficiencies.find(o => o.proficiency_id === obj_id);
    //   if (proficiency) {
    //     prof = true;
    //   } else {
    //     proficiency = char_interface.special_proficiencies.find(o => o.proficiency_id === obj_id);
    //     if (proficiency) {
    //       prof = true;
    //     }
    //   }
    // } else if (prof_type === "Tool") {
    //   const proficiency = char_interface.tool_proficiencies.find(o => o.proficiency_id === obj_id);
    //   if (proficiency) {
    //     prof = true;
    //   }
    // } 
    // return (
    //   <>
    //     <Grid
    //       className="vCentered"
    //       size={{
    //         xs: 4
    //       }}>
    //       { prof_obj.true_name }: { prof ? "Proficient" : "Not" }
    //     </Grid>
    //     <Grid
    //       size={{
    //         xs: customization.proficiency !== null ? 4 : 8
    //       }}>
    //       <SelectStringBox
    //         name="Proficiency"
    //         value={ customization.proficiency !== null ? (customization.proficiency ? "Proficient" : "Not Proficient") : "Not Custom" }
    //         options={["Not Custom", "Proficient", "Not Proficient"]}
    //         onChange={(changed: string) => {
    //           if (changed === "Not Custom") {
    //             char_interface.non_skill_prof_customizations[prof_type] = char_interface.non_skill_prof_customizations[prof_type].filter(o => o.id !== obj_id);
    //           } else {
    //             const new_prof = changed === "Proficient" ? true : false;
    //             if (!char_interface.non_skill_prof_customizations[prof_type]) {
    //               char_interface.non_skill_prof_customizations[prof_type] = [];
    //             }
    //             const cust = char_interface.non_skill_prof_customizations[prof_type].find(o => o.id === obj_id);
    //             if (cust) {
    //               cust.proficiency = new_prof;
    //             } else {
    //               char_interface.non_skill_prof_customizations[prof_type].push({ id: obj_id, proficiency: new_prof, notes: "" });
    //             }
    //           }
    //           character.subscription_holder.recalculate();
    //           setReloading(true);
    //         }}
    //       />
    //     </Grid>
    //     { customization.proficiency !== null &&
    //       <Grid
    //         size={{
    //           xs: 4
    //         }}>
    //         <StringBox
    //           name={ `Source/Notes` }
    //           value={ customization.notes }
    //           onBlur={(changed: string) => {
    //             const cust = char_interface.non_skill_prof_customizations[prof_type].find(o => o.id === obj_id);
    //             if (cust) {
    //               cust.notes = changed;
    //             }
    //             character.subscription_holder.recalculate();
    //             setReloading(true);
    //           }}
    //         />
    //       </Grid>
    //     }
    //   </>
    // );
    return null;
  }

  // const renderWeaponMasteryCustomizations = () => {
  //   return (
  //     <>
  //       <Grid item xs={12} className="header centered">
  //         { view }
  //       </Grid>
  //       <Grid item xs={12} container spacing={0} direction="row">
  //         { weapons.map((weapon, key) => {
  //           return (
  //             <Grid item xs={12} key={key} container spacing={0} direction="row">{ renderWeaponMasteryCustomization(weapon) }</Grid>
  //           );
  //         })}
  //       </Grid>
  //     </>
  //   );
  // }
  
  // const renderWeaponMasteryCustomization = (weapon: Weapon) => {
  //   const wm = character.weapon_masteries.find(o => o.base_item_id === weapon._id);
  //   let wmp_ids: string[] = [];
  //   if (wm) {
  //     wmp_ids = wm.wmp_ids;
  //     if (wmp_ids.length === 0) {
  //       const weapon = weapons.find(o => o._id === wm.base_item_id);
  //       if (weapon) {
  //         wmp_ids = [weapon.weapon_mastery_property_id];
  //       }
  //     }
  //   }
  //   const customization = character.weapon_mastery_customizations.find(o => o.base_item_id === weapon._id);
  //   return (
  //     <>
  //       <Grid item xs={4}>
  //         { weapon.true_name }:&nbsp;
  //         <DisplayObjects type="weapon_mastery_property" ids={wmp_ids} />
  //       </Grid>
  //       <Grid item xs={8}>
  //         { !reloading && 
  //           <SelectObjectBox
  //             name="Mastery Override"
  //             system="dandd"
  //             data_type="weapon_mastery_property"
  //             extra_options={["Not Custom"]}
  //             values={ customization ? (customization.wmp_ids) : ["Not Custom"] }
  //             multiple
  //             onChange={(changed: string[]) => {
  //               if (customization && changed.includes("Not Custom")) {
  //                 character.weapon_mastery_customizations = character.weapon_mastery_customizations.filter(o => o.base_item_id !== weapon._id);
  //               } else if (customization) {
  //                 customization.wmp_ids = changed;
  //                 // if (changed.length > 1) {
  //                 //   customization.improvement_type = "Merge";
  //                 // } else if (changed.length === 1) {
  //                 //   if (changed[0] === weapon.weapon_mastery_property_id) {
  //                 //     customization.improvement_type = "None";
  //                 //   } else {
  //                 //     customization.improvement_type = "Change";
  //                 //   }
  //                 // }
  //               } else {
  //                 changed = changed.filter(o => o !== "Not Custom");
  //                 const new_cust = new CharacterWeaponMastery();
  //                 new_cust.base_item_id = weapon._id;
  //                 new_cust.wmp_ids = changed;
  //                 // if (changed.length > 1) {
  //                 //   new_cust.improvement_type = "Merge";
  //                 // } else if (changed.length === 1) {
  //                 //   if (changed[0] === weapon.weapon_mastery_property_id) {
  //                 //     new_cust.improvement_type = "None";
  //                 //   } else {
  //                 //     new_cust.improvement_type = "Change";
  //                 //   }
  //                 // }
  //                 character.weapon_mastery_customizations.push(new_cust);
  //               }
  //               onChange();
  //               setReloading(true);
  //             }}
  //           />
  //         }
  //       </Grid>
  //     </>
  //   );
  // }

  const renderView = () => {
    switch(view) {
      // case "Current": return renderCurrent();
      case "Features": return renderProficiencyFeatures();
      // case "Bonus Proficiencies": return renderBonusProficiencyProfs();
      // case "Customize Weapon Masteries": return renderWeaponMasteryCustomizations();
      default: return renderProficiencyCustomizations();
    }
  }

  const renderControls = () => {
    return (
      <Grid
        container
        spacing={0}
        direction="row"
        size={{
          xs: 12
        }}>
        <Grid
          className="centered header"
          size={{
            xs: 12
          }}>
          Manage Proficiencies
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <FloatMenu
            options={[
              "Features",
              "Customize Armors","Customize Weapons","Customize Tools",
              // "Customize Weapon Masteries"
            ]}
            selected={view}
            onSelect={setView}
          />
        </Grid>
        { renderView() }
      </Grid>
    );
  }
  
  if (!fetched) {
    return (<LoadingBox />);
  } else {
    // console.log(character.weapon_masteries);
    return (
      <Grid container spacing={0} direction="row" className="curved_border bg_black_transp">
        <Grid
          className={`centered header ${ disabled ? "" : "clickable" }`}
          onClick={() => {
              if (!disabled) {
                setOpen(true); 
              } 
            }}
          size={{
            xs: 12
          }}>
          Proficiencies
        </Grid>
        { char_interface.mind.armor_proficiencies.length > 0 &&
          <>
            <Grid
              style={{ 
                fontWeight: "bold",
                fontSize: "15px", 
                paddingLeft: "4px" 
              }}
              size={{
                xs: 12
              }}>
              Armor
            </Grid>
            <Grid
              style={{
                fontSize: "11px", paddingLeft: "4px"
              }}
              size={{
                xs: 12
              }}>
              <DisplayObjects type="armor_type" ids={char_interface.mind.armor_proficiencies.map(o => { return o.proficiency_id; })} />
            </Grid>
          </>
        }
        { (char_interface.mind.weapon_proficiencies.length > 0 || 
            char_interface.mind.special_proficiencies.length > 0) && 
          <>
            <Grid
              style={{ 
                fontWeight: "bold",
                fontSize: "15px",
                borderTop: "1px solid", 
                paddingLeft: "4px"
              }}
              size={{
                xs: 12
              }}>
              Weapons
            </Grid>
            { char_interface.mind.weapon_proficiencies.length > 0 &&
              <Grid
                style={{
                  fontSize: "11px", paddingLeft: "4px"
                }}
                size={{
                  xs: 12
                }}>
                <DisplayObjects types={["weapon_keyword","item"]} ids={char_interface.mind.weapon_proficiencies.map(o => { return o.proficiency_id; })} />
              </Grid>
            }
          </>
        }
        { character.mind.weapon_masteries.length > 0 &&
          <>
            <Grid
              style={{ 
                fontWeight: "bold",
                fontSize: "15px",
                borderTop: "1px solid", 
                paddingLeft: "4px" 
              }}
              size={{
                xs: 12
              }}>
              Weapon Masteries
            </Grid>
            <Grid
              style={{
                fontSize: "11px", paddingLeft: "4px"
              }}
              size={{
                xs: 12
              }}>
              { character.mind.standard_weapon_masteries.map((cwm, key) => {
                const limit = cwm.weapon_limits.find(o => o.isEmpty(["item_ids"]));
                if (limit) {
                  // console.log(cwm);
                  // console.log(limit);
                  return (
                    <span key={cwm.true_id}>
                      { key > 0 && "; " }
                      <DisplayObjects type="item" ids={limit.item_ids} />:&nbsp;
                      <DisplayObjects type="weapon_mastery_property" ids={[cwm.source_id]} />
                    </span>
                  );
                }
                return null;
              })}
            </Grid>
          </>
        }
        { char_interface.mind.tool_proficiencies.length > 0 &&
          <>
            <Grid
              style={{ 
                fontWeight: "bold",
                fontSize: "15px",
                borderTop: "1px solid", 
                paddingLeft: "4px"
              }}
              size={{
                xs: 12
              }}>
              Tools
            </Grid>
            <Grid
              style={{
                fontSize: "11px", paddingLeft: "4px"
              }}
              size={{
                xs: 12
              }}>
              <DisplayObjects type="tool" ids={char_interface.mind.tool_proficiencies.map(o => { return o.proficiency_id; })} />
            </Grid>
          </>
        }
        <ModalOrDrawer
          open={ open } 
          onClose={() => {
            setOpen(false);
            character.subscription_holder.modalClosed();
          }}>
          { renderControls() }
        </ModalOrDrawer>
      </Grid>
    );
  }
}
